import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Plus, Eye, Pencil, Download, Search } from "lucide-react";
import { toast } from "sonner";
import { API, formatApiError, exportUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import StatusBadge from "../components/StatusBadge";
import Pagination from "../components/Pagination";
import { PersonForm } from "../components/forms";
import PrintButton from "../components/PrintButton";
import PrintHeader from "../components/PrintHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PROVINCES, PERSON_STATUTS, STATUT_LABELS } from "../lib/constants";

export default function Drivers() {
  const { hasPerm } = useAuth();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [province, setProvince] = useState("");
  const [statut, setStatut] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback((p = page, ps = pageSize, query = q, prov = province, st = statut) => {
    setLoading(true);
    API.get("/drivers", { params: { q: query, province: prov, statut: st, page: p, page_size: ps } })
      .then((r) => { setResult(r.data); setPage(p); })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [page, pageSize, q, province, statut]);

  useEffect(() => {
    load(1, pageSize, params.get("q") || "", "", "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => { setEditing(null); setForm({ statut: "ACTIF", nationalite: "Gabonaise" }); setDialogOpen(true); };
  const openEdit = (row) => {
    setEditing(row.id);
    API.get(`/drivers/${row.id}`).then((r) => { setForm(r.data.person); setDialogOpen(true); });
  };

  const save = async () => {
    if (!form.nom || !form.prenom) {
      toast.error("Le nom et le prenom sont obligatoires.");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await API.put(`/drivers/${editing}`, form);
        toast.success("Conducteur mis a jour.");
      } else {
        await API.post("/drivers", form);
        toast.success("Conducteur cree.");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="drivers-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold">Conducteurs</h1>
          <p className="text-sm text-muted-foreground">Referentiel des conducteurs et detenteurs de permis.</p>
        </div>
        <div className="flex gap-2">
          {hasPerm("EXPORT_DATA") && <PrintButton testid="print-drivers" />}
          {hasPerm("EXPORT_DATA") && (
            <>
              <Button variant="outline" size="sm" asChild data-testid="export-csv">
                <a href={exportUrl("/exports/drivers?format=csv")}><Download className="h-4 w-4 mr-1" /> CSV</a>
              </Button>
              <Button variant="outline" size="sm" asChild data-testid="export-xlsx">
                <a href={exportUrl("/exports/drivers?format=xlsx")}><Download className="h-4 w-4 mr-1" /> Excel</a>
              </Button>
            </>
          )}
          {hasPerm("CREATE_DRIVER") && (
            <Button onClick={openCreate} className="bg-[#009E60] hover:bg-[#00804e] text-white" data-testid="driver-create">
              <Plus className="h-4 w-4 mr-1" /> Nouveau conducteur
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-3" data-testid="drivers-filters">
        <div className="relative flex-1 min-w-56">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load(1)}
            placeholder="Nom, prenom, telephone..." className="pl-9" data-testid="drivers-search" />
        </div>
        <Select value={province || "all"} onValueChange={(v) => { setProvince(v === "all" ? "" : v); load(1, pageSize, q, v === "all" ? "" : v, statut); }}>
          <SelectTrigger className="w-48" data-testid="drivers-province"><SelectValue placeholder="Province" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les provinces</SelectItem>
            {PROVINCES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statut || "all"} onValueChange={(v) => { setStatut(v === "all" ? "" : v); load(1, pageSize, q, province, v === "all" ? "" : v); }}>
          <SelectTrigger className="w-40" data-testid="drivers-statut"><SelectValue placeholder="Statut" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous statuts</SelectItem>
            {PERSON_STATUTS.map((s) => <SelectItem key={s} value={s}>{STATUT_LABELS[s]}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button onClick={() => load(1)} variant="secondary" data-testid="drivers-apply">Filtrer</Button>
      </div>

      {loading && !result ? (
        <div className="space-y-2">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
      ) : (
        <>
          {result && <PrintHeader title="Liste des conducteurs" subtitle={`${result.total.toLocaleString("fr-FR")} enregistrement(s) — page ${page}`} />}
          <div className="overflow-x-auto rounded-lg border bg-card">
            <table className="w-full text-sm">
              <thead className="bg-accent/60 text-left">
                <tr>{["Nom", "Prenom", "Telephone", "Permis", "Structure", "Immatriculation", "Vehicule", "Statut", "Actions"].map((h) => (
                  <th key={h} className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {(result?.items || []).map((r, i) => (
                  <tr key={r.id} className="border-t hover:bg-accent/40 transition-colors" data-testid={`driver-row-${i}`}>
                    <td className="px-3 py-2.5 font-semibold">{r.nom}</td>
                    <td className="px-3 py-2.5">{r.prenom}</td>
                    <td className="px-3 py-2.5 mono text-xs">{r.telephone || "-"}</td>
                    <td className="px-3 py-2.5 mono text-xs">{r.permis || "-"}</td>
                    <td className="px-3 py-2.5 text-xs max-w-40 truncate">{r.structure || "-"}</td>
                    <td className="px-3 py-2.5 mono text-xs">{r.immatriculation || "-"}</td>
                    <td className="px-3 py-2.5 text-xs">{r.vehicule || "-"}</td>
                    <td className="px-3 py-2.5"><StatusBadge value={r.statut} /></td>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      <Button asChild variant="ghost" size="sm" data-testid={`driver-view-${i}`}>
                        <Link to={`/conducteurs/${r.id}`}><Eye className="h-4 w-4" /></Link>
                      </Button>
                      {hasPerm("UPDATE_DRIVER") && (
                        <Button variant="ghost" size="sm" onClick={() => openEdit(r)} data-testid={`driver-edit-${i}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
                {result && result.items.length === 0 && (
                  <tr><td colSpan={9} className="px-3 py-10 text-center text-muted-foreground">Aucun conducteur trouve.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {result && (
            <Pagination total={result.total} page={page} pageSize={pageSize}
              onPage={(p) => load(p)} onPageSize={(s) => { setPageSize(s); load(1, s); }} testid="drivers-pagination" />
          )}
        </>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="driver-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Modifier le conducteur" : "Nouveau conducteur"}</DialogTitle>
          </DialogHeader>
          <PersonForm initial={form} onChange={setForm} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="driver-cancel">Annuler</Button>
            <Button onClick={save} disabled={saving} className="bg-[#009E60] hover:bg-[#00804e] text-white" data-testid="driver-save">
              {saving ? "Enregistrement..." : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
